import BuyNowCarDetailReadOnly from "@common/pages/Dashboard/components/BuyNow/BuyNowCarDetailReadOnly";
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { routes } from "../../../utils/constants/routes";
import BuyNowCarDetail from "../components/BuyNow/BuyNowCarDetail";
import BuyNowCarsList from "../components/BuyNow/BuyNowCarsList";
import { useBuyNowSocketEvents } from "../components/BuyNow/customHooks/useBuyNowAuctionSocketEvents";

const BuyNow = () => {
  // React Router hooks
  const navigate = useNavigate();
  const location = useLocation();

  // Component state
  const [selectedCar, setSelectedCar] = useState(null);
  
  // Extract tab state from URL search params (0 = My Cars, 1 = Other Cars)
  const myCarsOrOtherCars = useMemo(() => {
    const searchParams = new URLSearchParams(location.search);
    const tab = searchParams.get('tab');
    return tab !== null ? parseInt(tab, 10) : null;
  }, [location.search]);

  // Extract vehicle ID from pathname
  const vehicleId = useMemo(() => {
    const pathParts = location.pathname.split(`${routes.buyNow}/`);
    return pathParts.length > 1 ? pathParts[1] : null;
  }, [location.pathname]);

  // Redirect to base route if myCarsOrOtherCars is not set when trying to view vehicle detail
  useEffect(() => {
    if (myCarsOrOtherCars === null && vehicleId) {
      navigate(`${routes.dashboardNavigation}${routes.buyNow}`);
    }
  }, [myCarsOrOtherCars, vehicleId, navigate]);

  // Handler for car click - memoized to prevent unnecessary re-renders
  const handleCarClick = useCallback((car) => {
    setSelectedCar(car);
    // Preserve the tab parameter when navigating to detail view
    const searchParams = new URLSearchParams(location.search);
    const queryString = searchParams.toString();
    navigate(`${routes.dashboardNavigation}${routes.buyNow}/${car?.Vehicle_Id}${queryString ? `?${queryString}` : ''}`);
  }, [navigate, location.search]);

  // Handler for cars filter change - memoized to prevent unnecessary re-renders
  const handleMyCarsOrOtherCars = useCallback((value) => {
    // Update URL search params instead of local state
    navigate(`${routes.dashboardNavigation}${routes.buyNow}?tab=${value}`, { replace: true });
  }, [navigate]);

  // Render detail views when vehicle ID is present
  if (vehicleId && myCarsOrOtherCars !== null) {
    if (myCarsOrOtherCars === 0) {
      return (
        <BuyNowCarDetailReadOnly 
          useBuyNowSocketEvents={useBuyNowSocketEvents}
          selectedCar={selectedCar}
          setSelectedCar={setSelectedCar}
        />
      );
    }
    
    return (
      <BuyNowCarDetail 
        selectedCar={selectedCar}
        setSelectedCar={setSelectedCar}
      />
    );
  }

  // Default: render list view
  return (
    <BuyNowCarsList 
      selectedCar={selectedCar}
      setSelectedCar={setSelectedCar}
      onCarClick={handleCarClick}
      onMyCarsOrOtherCars={handleMyCarsOrOtherCars}
    />
  );
};

export default BuyNow;
